import { NavLink, useNavigate } from 'react-router-dom';
import { Moon, Sun, RefreshCw, MapPin, FileText, Scale, BarChart3, Loader2, Menu, X, Settings, LogOut, AlertCircle, ShieldAlert } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useTheme } from '@/components/theme-provider';
import { cn } from '@/lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useManualSync, useSyncEvents } from '@/features/unidades/hooks/useSyncEvents';
import { useAuth } from '@/features/auth';
import logoClaro from '@/img/Logo Claro.svg';
import { useState, useEffect, useRef } from 'react';

// Tempo estimado de sync em segundos (otimizado em 09/02/2026: 8→5 endpoints)
const ESTIMATED_SYNC_TIME = 60;

const navigation = [
  { name: 'Unidades', href: '/unidades', icon: MapPin },
  { name: 'Documentos', href: '/documentos', icon: FileText },
  { name: 'Processos', href: '/processos', icon: Scale },
  { name: 'Pendências', href: '/pendencias', icon: AlertCircle, separator: true },
  { name: 'Relatório', href: '/relatorio', icon: BarChart3 },
  { name: 'Auditoria', href: '/auditoria', icon: ShieldAlert },
];

export function Header() {
  const { theme, setTheme } = useTheme();
  const { triggerSync, instantRefresh, isTriggering, isRefreshing } = useManualSync();
  const { status: syncStatus } = useSyncEvents();
  const { user, logout } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  
  // Estado para barra de progresso
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const syncStartTimeRef = useRef<number | null>(null);
  
  // isRefreshing = refresh instantâneo, syncStatus.isSyncing = sync em background
  const isBusy = isTriggering || isRefreshing || syncStatus.isSyncing;
  const isSyncingBackground = syncStatus.isSyncing && !isRefreshing && !isTriggering;

  // Timer para barra de progresso
  useEffect(() => {
    if (syncStatus.isSyncing) {
      if (!syncStartTimeRef.current) {
        syncStartTimeRef.current = Date.now();
        setElapsedSeconds(0);
      }
      
      const interval = setInterval(() => {
        if (syncStartTimeRef.current) {
          setElapsedSeconds(Math.floor((Date.now() - syncStartTimeRef.current) / 1000));
        }
      }, 100);
      
      return () => clearInterval(interval);
    } else {
      syncStartTimeRef.current = null;
      setElapsedSeconds(0);
    }
  }, [syncStatus.isSyncing]);

  // Progresso baseado em tempo (0-100)
  const progress = Math.min((elapsedSeconds / ESTIMATED_SYNC_TIME) * 100, 95);

  const toggleTheme = () => {
    setTheme(theme === 'dark' ? 'light' : 'dark');
  };

  // Clique único = refresh instantâneo + sync em background
  const handleSync = async () => {
    if (isBusy) return;
    await triggerSync();
  };

  // Double-click = apenas refresh instantâneo (sem sync externo)
  const handleQuickRefresh = async () => {
    if (isBusy) return;
    await instantRefresh();
  };

  const handleLogout = () => {
    logout();
    navigate('/login', { replace: true });
  };

  return (
    <TooltipProvider delayDuration={0}>
      {/* Barra de progresso global - aparece no topo durante sync */}
      {syncStatus.isSyncing && (
        <div className="fixed top-0 left-0 right-0 z-50">
          <div className="h-1 bg-muted">
            <div
              className="h-full bg-blue-500 transition-all duration-300 ease-out"
              style={{ width: `${progress}%` }}
            />
          </div>
          <div className="absolute top-1 left-1/2 -translate-x-1/2 px-3 py-1 bg-blue-500 text-white text-xs rounded-b-lg shadow-lg flex items-center gap-2">
            <Loader2 className="h-3 w-3 animate-spin" />
            <span>Sincronizando... {Math.round(progress)}%</span>
            <span className="text-blue-200">~{Math.max(0, ESTIMATED_SYNC_TIME - elapsedSeconds)}s</span>
          </div>
        </div>
      )}
      
      <header className={cn(
        "flex h-14 items-center justify-between border-b bg-card px-3 md:px-4",
        syncStatus.isSyncing && "mt-1" // Espaço para a barra de progresso
      )}>
        {/* Logo */}
        <div className="flex items-center gap-2">
          <img 
            src={logoClaro} 
            alt="Claro" 
            className={cn(
              "h-5 md:h-6",
              theme === 'light' && "invert"
            )} 
          />
          <span className="text-base md:text-lg font-bold hidden sm:inline" style={{ paddingTop: '0.5rem', paddingLeft: '1rem' }}>Dashboard</span>
        </div>

        {/* Navigation - Desktop */}
        <nav className="hidden md:flex items-center gap-1">
          {navigation.map((item) => (
            <div key={item.name} className="flex items-center">
              <NavLink
                to={item.href}
                className={({ isActive }) =>
                  cn(
                    'flex flex-row items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                    'hover:bg-accent hover:text-accent-foreground',
                    isActive
                      ? 'bg-accent text-accent-foreground'
                      : 'text-muted-foreground'
                  )
                }
              >
                <item.icon className="h-4 w-4 shrink-0" />
                <span className="hidden lg:inline">{item.name}</span>
              </NavLink>
              {'separator' in item && item.separator && (
                <div className="mx-2 h-5 w-px bg-border" />
              )}
            </div>
          ))}
        </nav>

        {/* Actions */}
        <div className="flex items-center gap-1">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button 
                variant="ghost" 
                size="icon" 
                className={cn(
                  "h-8 w-8 transition-colors",
                  isSyncingBackground && "text-blue-500"
                )}
                onClick={handleSync}
                onDoubleClick={handleQuickRefresh}
                disabled={isBusy}
              >
                {isBusy ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className={cn(
                    "h-4 w-4",
                    isSyncingBackground && "animate-spin"
                  )} />
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              {isBusy 
                ? 'Atualizando...' 
                : isSyncingBackground 
                  ? 'Sincronizando em background...'
                  : 'Atualizar dados (duplo-clique: apenas refresh)'}
            </TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={toggleTheme}>
                {theme === 'dark' ? (
                  <Sun className="h-4 w-4" />
                ) : (
                  <Moon className="h-4 w-4" />
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              {theme === 'dark' ? 'Modo claro' : 'Modo escuro'}
            </TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-8 w-8" 
                onClick={() => navigate('/admin')}
              >
                <Settings className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              Área do Administrador
            </TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-8 w-8 text-red-500 hover:text-red-600 hover:bg-red-500/10" 
                onClick={handleLogout}
              >
                <LogOut className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              Sair ({user?.email})
            </TooltipContent>
          </Tooltip>

          {/* Menu Hamburger - Mobile */}
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 md:hidden"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
          </Button>
        </div>
      </header>

      {/* Mobile Navigation Menu */}
      {mobileMenuOpen && (
        <nav className="md:hidden border-b bg-card px-3 py-2">
          <div className="flex flex-col gap-1">
            {navigation.map((item) => (
              <NavLink
                key={item.name}
                to={item.href}
                onClick={() => setMobileMenuOpen(false)}
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors',
                    'hover:bg-accent hover:text-accent-foreground',
                    isActive
                      ? 'bg-accent text-accent-foreground'
                      : 'text-muted-foreground'
                  )
                }
              >
                <item.icon className="h-4 w-4" />
                <span>{item.name}</span>
              </NavLink>
            ))}
          </div>
        </nav>
      )}
    </TooltipProvider>
  );
}
